import React, { useState, useEffect } from 'react';
import { MessageSquare, FileText, Paperclip } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import { communicationService } from '../../../services/communicationService';

const MessageComposer = ({ 
  content, 
  onContentChange, 
  deliveryMethod, 
  onDeliveryMethodChange,
  scheduledTime,
  onScheduledTimeChange,
  recipientCount,
  selectedTemplate,
  onTemplateChange
}) => {
  const [attachments, setAttachments] = useState([]);
  const [templateVariables, setTemplateVariables] = useState({});
  const [renderedMessage, setRenderedMessage] = useState('');

  useEffect(() => {
    if (selectedTemplate) {
      // Runtime message: user will type message
      if (selectedTemplate.is_runtime_message) {
        setRenderedMessage(content || '');
        // Don't auto-fill content for runtime messages
        if (!content) {
          onContentChange('');
        }
      } else if (selectedTemplate.is_static_message) {
        setRenderedMessage(selectedTemplate.template_message || '');
        onContentChange(selectedTemplate.template_message || '');
      } else {
        // Initialize variables for templated messages
        const vars = {};
        (selectedTemplate.templated_vars || []).forEach(v => {
          vars[v] = '';
        });
        setTemplateVariables(vars);
      }
    } else {
      setTemplateVariables({});
      setRenderedMessage(content);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (selectedTemplate && !selectedTemplate.is_static_message && !selectedTemplate.is_runtime_message) {
      renderTemplate();
    }
  }, [templateVariables, selectedTemplate]);

  const renderTemplate = async () => {
    if (!selectedTemplate || selectedTemplate.is_static_message || selectedTemplate.is_runtime_message) return;
    
    try {
      const message = await communicationService.renderTemplate(selectedTemplate, templateVariables);
      setRenderedMessage(message);
      onContentChange(message);
    } catch (error) {
      console.error('Error rendering template:', error);
    }
  };

  const deliveryMethods = [
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { id: 'sms', label: 'SMS', icon: MessageSquare },
    { id: 'both', label: 'WhatsApp & SMS', icon: MessageSquare }
  ];

  const handleAttachment = (e) => {
    const files = Array.from(e?.target?.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const handleVariableChange = (varName, value) => {
    setTemplateVariables(prev => ({ ...prev, [varName]: value }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Compose Message</h3>
        <p className="text-sm text-gray-600 mt-1">
          Sending to {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
        </p>
        {selectedTemplate && (
          <p className="text-xs text-blue-600 mt-1">
            Using template: {selectedTemplate.name}
          </p>
        )}
      </div>
      <div className="p-4 space-y-4">
        {/* Delivery Method Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Delivery Method
          </label>
          <div className="flex gap-2">
            {deliveryMethods?.map(method => {
              const Icon = method?.icon;
              return (
                <button
                  key={method?.id}
                  onClick={() => onDeliveryMethodChange(method?.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                    deliveryMethod === method?.id
                      ? 'bg-blue-50 border-blue-300 text-blue-700' :'bg-white border-gray-300 text-gray-700 hover:border-blue-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium text-sm">{method?.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Template Variables (if templated) */}
        {selectedTemplate && !selectedTemplate.is_static_message && selectedTemplate.templated_vars?.length > 0 && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Template Variables</h4>
            <div className="space-y-3">
              {selectedTemplate.templated_vars.map(varName => (
                <div key={varName}>
                  <label className="block text-xs font-medium text-blue-700 mb-1">
                    {varName}
                  </label>
                  <input
                    type="text"
                    value={templateVariables[varName] || ''}
                    onChange={(e) => handleVariableChange(varName, e.target.value)}
                    placeholder={`Enter ${varName}`}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Message Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {selectedTemplate?.is_runtime_message 
              ? 'Message Content' 
              : selectedTemplate 
                ? 'Rendered Message' 
                : 'Message Content'}
          </label>
          {selectedTemplate?.is_runtime_message ? (
            <textarea
              value={content}
              onChange={(e) => onContentChange(e.target.value)}
              placeholder="Type your message here..."
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          ) : selectedTemplate ? (
            <div className="px-4 py-3 border border-gray-300 rounded-lg bg-gray-50">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{renderedMessage || 'Fill in variables above to see rendered message'}</p>
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => onContentChange(e?.target?.value)}
              placeholder="Type your message here..."
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">
              {renderedMessage?.length || content?.length || 0} characters
            </span>
            <span className="text-sm text-gray-500">
              {deliveryMethod === 'sms' && (renderedMessage?.length || content?.length || 0) > 160 && 
                `${Math.ceil((renderedMessage?.length || content?.length || 0) / 160)} SMS segments`
              }
            </span>
          </div>
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attachments (WhatsApp only)
          </label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <Paperclip className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-700">Add File</span>
              <input
                type="file"
                multiple
                onChange={handleAttachment}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </label>
            {attachments?.length > 0 && (
              <span className="text-sm text-gray-600">
                {attachments?.length} file{attachments?.length !== 1 ? 's' : ''} attached
              </span>
            )}
          </div>
          {attachments?.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments?.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm text-gray-700">{file?.name}</span>
                  <button
                    onClick={() => setAttachments(prev => prev?.filter((_, i) => i !== index))}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Schedule Options */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Schedule Message (Optional)
          </label>
          <div className="flex gap-3">
            <input
              type="datetime-local"
              value={scheduledTime || ''}
              onChange={(e) => onScheduledTimeChange(e?.target?.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {scheduledTime && (
              <button
                onClick={() => onScheduledTimeChange(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Preview */}
        {(renderedMessage || content) && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Message Preview</span>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{renderedMessage || content}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageComposer;
